import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";
import VideocamIcon from "@material-ui/icons/Videocam";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import SearchIcon from "@material-ui/icons/Search";

const TAB_ALL = 0;
const TAB_TEXT = 1;
const TAB_MEDIA = 2;
const TAB_LOCATION = 3;
const TYPE_MAP = ["", "text", "media", "location"];
const DEBOUNCE_MS = 250;

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 1300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  panel: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[24],
    width: "100%",
    maxWidth: 640,
    minWidth: 400,
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  title: {
    padding: "16px 20px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    fontWeight: 600,
    fontSize: 18,
  },
  tabs: {
    display: "flex",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    flexShrink: 0,
    "& button": {
      flex: 1,
      minWidth: 0,
      border: "none",
      padding: "8px 4px",
      fontSize: "0.75rem",
      cursor: "pointer",
      background: "none",
      color: theme.palette.text.secondary,
      "&.active": {
        color: theme.palette.primary.main,
        fontWeight: 600,
        borderBottom: "2px solid " + theme.palette.primary.main,
      },
    },
  },
  list: {
    flex: 1,
    overflowY: "auto",
    minHeight: 200,
    padding: 8,
  },
  item: {
    display: "flex",
    alignItems: "center",
    padding: "10px 8px",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  thumb: {
    width: 48,
    height: 48,
    objectFit: "cover",
    borderRadius: 8,
    marginRight: 12,
    flexShrink: 0,
  },
  iconWrap: {
    width: 48,
    height: 48,
    marginRight: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: theme.palette.text.secondary,
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    padding: "10px 16px",
    borderBottom: "1px solid rgba(0,0,0,0.12)",
    backgroundColor: theme.palette.background.default,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 8,
    padding: "10px 14px 10px 40px",
    fontSize: 15,
    outline: "none",
    "&:focus": {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
  },
  searchIcon: {
    position: "absolute",
    left: 28,
    color: theme.palette.text.secondary,
    pointerEvents: "none",
  },
}));

export default function QuickReplyPicker({
  open,
  searchTerm,
  onClose,
  onSelectText,
  onSelectQuickReply,
  ticketId,
}) {
  const classes = useStyles();
  const [tabIndex, setTabIndex] = useState(TAB_ALL);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Sync search from parent when dialog opens (e.g. "/hello" from main input)
  useEffect(() => {
    if (open) {
      setLocalSearch(searchTerm || "");
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open, searchTerm]);

  const fetchList = useCallback(async (term, tab) => {
    setLoading(true);
    try {
      const params = { searchParam: (term || "").trim(), pageNumber: "1" };
      if (TYPE_MAP[tab]) params.type = TYPE_MAP[tab];
      const { data } = await api.get("/quickAnswers/", { params });
      setList(data.quickAnswers || []);
    } catch (err) {
      toastError(err);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch when open, localSearch or tab changes (debounce search; immediate when opening)
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    const term = (localSearch || "").trim();
    const justOpened = !prevOpenRef.current;
    prevOpenRef.current = true;

    if (justOpened) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      fetchList(term, tabIndex);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchList(term, tabIndex);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, localSearch, tabIndex, fetchList]);

  const handleItemClick = async (item) => {
    if (item.type === "text" || !item.type) {
      onSelectText(item.message || "");
      onClose();
      return;
    }
    if (item.type === "media" || item.type === "location") {
      try {
        await api.post(`/messages/${ticketId}/sendQuickReply`, {
          quickAnswerId: item.id,
        });
        onSelectQuickReply();
        onClose();
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  const normalizedSearch = (localSearch || "").trim().toLowerCase();
  const filteredList = normalizedSearch
    ? list.filter((item) => {
        const title =
          (item.title || item.shortcut || "").toString().toLowerCase();
        return title.includes(normalizedSearch);
      })
    : list;

  const tabLabels = [
    i18n.t("messagesInput.quickReplyPicker.tabs.all"),
    i18n.t("messagesInput.quickReplyPicker.tabs.text"),
    i18n.t("messagesInput.quickReplyPicker.tabs.media"),
    i18n.t("messagesInput.quickReplyPicker.tabs.location"),
  ];

  return (
    <div className={classes.overlay} onClick={handleBackdropClick}>
      <div className={classes.panel} onClick={(e) => e.stopPropagation()}>
        <div className={classes.title}>
          {i18n.t("mainDrawer.listItems.quickAnswers")}
        </div>
        <div className={classes.searchWrap} style={{ position: "relative" }}>
          <SearchIcon className={classes.searchIcon} fontSize="small" />
          <input
            ref={searchInputRef}
            type="text"
            className={classes.searchInput}
            placeholder={i18n.t("messagesInput.quickReplyPicker.searchPlaceholder")}
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        <div className={classes.tabs}>
        {tabLabels.map((label, idx) => (
          <button
            key={idx}
            type="button"
            className={tabIndex === idx ? "active" : ""}
            onClick={() => setTabIndex(idx)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={classes.list}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <CircularProgress size={32} />
          </div>
        ) : filteredList.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "gray" }}>
            {i18n.t("messagesInput.quickReplyPicker.noResults")}
          </div>
        ) : (
          filteredList.map((item) => (
            <div
              key={item.id}
              className={classes.item}
              onClick={() => handleItemClick(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleItemClick(item);
                }
              }}
            >
              {item.type === "media" &&
              item.mediaType === "image" &&
              item.mediaPath ? (
                <img
                  src={`${getBackendUrl()}public/${item.mediaPath}`}
                  alt=""
                  className={classes.thumb}
                />
              ) : item.type === "media" ? (
                <div className={classes.iconWrap}>
                  {item.mediaType === "video" ? (
                    <VideocamIcon fontSize="large" />
                  ) : (
                    <InsertDriveFileIcon fontSize="large" />
                  )}
                </div>
              ) : item.type === "location" ? (
                <div className={classes.iconWrap}>
                  <LocationOnIcon fontSize="large" />
                </div>
              ) : (
                <div className={classes.iconWrap}>
                  <TextFieldsIcon fontSize="large" />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>
                  {item.title || item.shortcut || item.message || "-"}
                </div>
                {item.type === "text" && item.message && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "gray",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.message}
                  </div>
                )}
                {item.type === "location" && item.locationName && (
                  <div style={{ fontSize: 12, color: "gray" }}>
                    {item.locationName}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
}
